import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { FlyingTulipMarketsRaw } from './types'

/**
 * The ONE Flying Tulip lender key per chain.
 *
 * Flying Tulip is a CROSS-MARGIN protocol: a single per-chain
 * `PositionsManager` holds every asset, health is one global check
 * (`HF = equity / Σ bothLegs × mmBps`), and there is no per-market key space
 * to fan out. So it is modeled exactly like Exactly post-collapse: ONE lender
 * key, one entry per asset.
 */
export const FLYING_TULIP_LENDER_KEY = 'FLYING_TULIP'

function toHuman(raw: bigint, decimals: number): number {
  return Number(raw) / 10 ** decimals
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  name: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name, decimals }
}

/**
 * Map the lens batch into the shared `MorphoGeneralPublicResponse` shape,
 * under the SINGLE cross-margin {@link FLYING_TULIP_LENDER_KEY} — one entry
 * per ASSET.
 *
 * Risk factors (FLYING_TULIP.md §2.1): `maint` charges mmBps on BOTH legs, so
 * pair LTV is multiplicative like Exactly's Auditor — but the open gate and
 * the liquidation trigger use DIFFERENT HF thresholds (1.50 / 1.25), and our
 * `config[0]` carries a single `borrowFactor`. The build uses `hfSafe` for
 * `borrowFactor`, which makes the OPEN gate exact
 * (`borrowCollateralFactor_i × borrowFactor_j` = the protocol's max LTV to
 * the basis point) and leaves the liquidation threshold conservative by
 * ~0.35 pp on stables — deliberate: quotes never revert, warnings come early.
 *
 *   borrowCollateralFactor_i = 1 − hfSafe·mm_i        (open)
 *   collateralFactor_i       = 1 − hfTgt ·mm_i        (liquidation)
 *   borrowFactor_j           = 1 / (1 + hfSafe·mm_j)
 *
 * `depositRate = variableBorrowRate × utilization`, with NO reserve factor.
 *
 * This CORRECTS an earlier reading (FLYING_TULIP.md §3.1) that supplier yield
 * is only an epoch-indexed FT emission and that any IRM-derived supply APR is
 * therefore wrong. The FT emission is real and is still not modelled here — it
 * needs an external FT price and belongs in the rewards pipeline — but it is
 * ON TOP of in-kind interest, not instead of it. Flying Tulip's own UI computes
 * the supply leg exactly as borrow × utilization, verified against the live
 * Sonic book on 6/6 assets (2026-08-26):
 *
 *   USDC  94.26 % util → 10.7609 % borrow → 10.1434 %   (their UI: 10.14 %)
 *   ftUSD 87.96 %      →  6.3527 %        →  5.5879 %   (5.58 %)
 *   wS    42.57 %      →  2.1453 %        →  0.9132 %   (0.91 %)
 *   USSD  14.38 %      →  3.1921 %        →  0.4591 %   (0.45 %)
 *   WBTC   1.74 %      →  1.1337 %        →  0.0197 %   (0.01 %, they truncate)
 *   WETH   1.67 %      →  1.1283 %        →  0.0188 %   (0.01 %)
 *
 * Two things this pins down. There is **no reserve factor** today — the naive
 * `borrow × util` matches to four decimals, so do not introduce a `(1 − rf)`
 * term on the assumption that every pool has one; `reserves` on `astate` is an
 * accrued balance, not a rate. And their column is LABELLED "Deposit APY" but
 * carries the APR (10.14 %, not the 10.67 % that compounds to) — so emit it as
 * nominal APR per the package convention and do not compound it.
 *
 * Use the lens's own `utilWad` as the denominator. `borrows / totalSupplied`
 * agrees to four decimals on every asset, but `utilWad` is the figure the
 * protocol itself feeds to the IRM, so the two legs cannot drift apart.
 */
export function convertFlyingTulipMarketsToResponse(
  raw: FlyingTulipMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const assets = raw?.assets ?? []
  if (assets.length === 0) return out

  const lenderKey = FLYING_TULIP_LENDER_KEY
  const hfSafe = raw.hfSafeBps / 1e4
  const hfTgt = raw.hfTargetBps / 1e4
  // @ts-ignore — built incrementally, matches the Morpho-shaped converters
  const entry: MorphoGeneralPublicResponse = { data: {} }

  for (const a of assets) {
    const assetAddr = a.address
    const decimals = a.decimals

    // Protocol oracle first (1e18 USD via `priceAndDecimals`); the shared
    // price map covers the protocol-unpriceable FT token.
    const token = tokens[assetAddr]
    const priceKey =
      toOracleKey(token?.assetGroup!) || toGenericPriceKey(assetAddr, chainId)
    const price =
      (a.priceWad != null ? Number(a.priceWad) / 1e18 : 0) ||
      (prices[priceKey] ?? 0)

    // Compound-style totals: supplied = cash + borrows − reserves.
    const totalDeposits = toHuman(a.cash + a.borrows - a.reserves, decimals)
    const totalDebt = toHuman(a.borrows, decimals)
    const borrowLiquidity = toHuman(a.cash, decimals)
    const utilization = Number(a.utilWad) / 1e18

    const mm = a.mmBps / 1e4
    const borrowCollateralFactor = Math.max(0, 1 - hfSafe * mm)
    const collateralFactor = Math.max(0, 1 - hfTgt * mm)
    const borrowFactor = 1 / (1 + hfSafe * mm)

    const variableBorrowRate =
      a.borrowAprWad != null ? (Number(a.borrowAprWad) / 1e18) * 100 : 0

    const borrowingEnabled = a.enabled && a.borrowable && !a.borrowPaused
    const collateralActive = a.enabled && a.isCollateral

    const loanUid = createMarketUid(chainId, lenderKey, assetAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: a.symbol,
      poolId: raw.positionsManager,
      underlying: assetAddr,
      asset: currencyFor(assetAddr, decimals, a.symbol, a.name, tokens),
      totalDeposits,
      totalDebtStable: 0,
      totalDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * price,
      borrowLiquidityUSD: borrowLiquidity * price,
      totalDepositsUSD: totalDeposits * price,
      totalDebtStableUSD: 0,
      totalDebtUSD: totalDebt * price,
      utilization,
      // Supply leg = borrow × utilization, no reserve factor. Reproduces Flying
      // Tulip's own "Deposit APY" column exactly — see the doc comment for the
      // 6/6 verification and for why this is an APR despite their label.
      depositRate: variableBorrowRate * utilization,
      variableBorrowRate,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor,
          collateralFactor,
          borrowFactor,
          // Liquidation is a permissioned RFQ fill (no fixed on-chain bonus).
          liquidationPenalty: 0,
          closeFactor: 1,
          collateralDisabled: !collateralActive,
          debtDisabled: !borrowingEnabled,
        },
      },
      closeFactor: 1,
      collateralActive,
      borrowingEnabled,
      depositsEnabled: a.enabled && !a.depositPaused,
      hasStable: false,
      variableBorrowDisabled: !borrowingEnabled,
      isActive: a.enabled,
      isFrozen: !a.enabled,
      // Per-asset Flying Tulip extras (caps are raw base units).
      flyingTulip: {
        irm: a.irm,
        mmBps: a.mmBps,
        supplyCap: a.supplyCap.toString(),
        borrowCap: a.borrowCap.toString(),
        withdrawPaused: a.withdrawPaused,
        // FT itself: oracle-unpriceable, `collateral: false, mmBps: 0` — it
        // contributes nothing to equity/maintenance and must never become a
        // collateral row.
        unpriceable: !a.priceable,
      },
    } as any
  }

  // ONE market descriptor for the whole cross-margin pool: the
  // PositionsManager IS the pool's identity (the Auditor seam in Exactly).
  entry.params = {
    market: {
      lender: lenderKey,
      name: 'Flying Tulip',
      id: raw.positionsManager,
      // No market-level LLTV: risk is per-asset and pair LTV multiplicative.
      lltv: '0',
      oracle: raw.oracleRouter || zeroAddress,
      irm: zeroAddress,
      collateralAddress: zeroAddress,
      loanAddress: zeroAddress,
      flyingTulip: {
        hfSafeBps: raw.hfSafeBps,
        hfTargetBps: raw.hfTargetBps,
        // PER CHAIN ($250 Ethereum / $10 Sonic) — read, never pinned.
        minEquityUSDWad: raw.minEquityUSDWad,
      },
    } as any,
  }
  entry.chainId = chainId
  out[lenderKey] = entry

  return out
}
